import React from "react";
import TruckCompatibilityGroup from "../TruckCompatibilityGroup.jsx";
import {
  EmptyState,
} from "../ResultsPrimitives.jsx";
import ReassignmentLoadingState from "./ReassignmentLoadingState.jsx";

function InlineStatusNotice({ status }) {
  if (!status) {
    return null;
  }

  const isLoading = status?.type === "loading";
  const detailItems = Array.isArray(status?.detailItems) ? status.detailItems : [];

  return (
    <div
      aria-live="polite"
      className={[
        "mb-3 rounded-lg border px-3 py-2.5 shadow-none",
        isLoading
          ? "border-border-accent-subtle bg-accent-soft"
          : "border-border-danger-muted bg-danger-soft",
      ].join(" ")}
      role="status"
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden="true"
          className={[
            "relative mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border bg-white shadow-none",
            isLoading ? "border-border-accent-subtle" : "border-border-danger-subtle",
          ].join(" ")}
        >
          {isLoading ? (
            <>
              <span className="absolute inset-1.5 rounded-full border-2 border-dashed border-accent/25 animate-spin" />
              <span className="absolute inset-3 rounded-full border border-accent/12" />
              <span className="relative h-2.5 w-2.5 rounded-full bg-accent ring-4 ring-accent/10 animate-pulse" />
            </>
          ) : (
            <span className="h-3 w-3 rounded-full bg-danger ring-4 ring-danger/10" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text">
            {status?.title || "Mise à jour en cours"}
          </p>
          <p className="mt-0.5 text-sm leading-5 text-muted-strong">
            {status?.message || "La fenêtre est en train de mettre à jour la réaffectation."}
          </p>
          {detailItems.length ? (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {detailItems.map((item) => (
                <div
                  key={item.key || item.label}
                  className="rounded-lg border border-line-soft bg-white px-2.5 py-2 shadow-none"
                >
                  <div className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-500">
                    {item.label}
                  </div>
                  <div className="mt-1 text-sm font-semibold leading-5 text-text">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function ReassignmentPopoverContent({
  hasDisplayedCompatibilities,
  impactCalculationStatus,
  informationText,
  inlineStatus,
  isLoading,
  onReassign,
  remoteError,
  resolvedContexts,
  resolvedStep,
  shouldShowLoadingState,
  shouldRenderCompatibilities,
  showRouteImpact,
}) {
  const activeInlineStatus = inlineStatus || impactCalculationStatus;
  const interactionsDisabled = activeInlineStatus?.type === "loading";

  return (
    <div className="max-h-[calc(100dvh-6rem)] overflow-y-auto bg-surface px-3 py-1 lg:px-4 lg:py-1">
      {shouldShowLoadingState ? <ReassignmentLoadingState showRouteImpact={showRouteImpact} /> : null}
      <InlineStatusNotice status={activeInlineStatus} />
      {remoteError ? (
        <div className="mb-4 rounded-xl border border-danger bg-danger-soft px-4 py-3 text-sm font-medium text-danger shadow-card">
          {remoteError}
        </div>
      ) : null}
      {!remoteError && !showRouteImpact ? (
        <div className="mb-2 rounded-xl border border-line-soft bg-surface-muted px-3 py-2 text-sm leading-5 text-muted-strong shadow-none">
          {informationText}
        </div>
      ) : null}
      {shouldRenderCompatibilities ? (
        <div className={showRouteImpact ? "grid gap-0.5" : "grid gap-2"}>
          {resolvedContexts.map((context, index) => (
            <TruckCompatibilityGroup
              key={context?.delivery_id ?? context?.order_id ?? `context-${index}`}
              context={context}
              interactionsDisabled={interactionsDisabled}
              onReassign={onReassign}
              selectedDeliveryId={resolvedStep?.delivery_id ?? null}
              showHeader={resolvedContexts.length > 1}
              showRouteImpact={showRouteImpact}
            />
          ))}
        </div>
      ) : null}
      {!isLoading && !remoteError && !hasDisplayedCompatibilities ? (
        <EmptyState
          tone="subtle"
          message={showRouteImpact
            ? "Aucun camion compatible disponible pour cette étape."
            : "Aucun camion compatible n'est disponible dans la vue rapide pour cette étape."}
          className="text-sm leading-6"
        />
      ) : null}
    </div>
  );
}
